import { createContext, useContext } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { STAGGER, transitions } from '@/lib/motion'

const FadeInStaggerContext = createContext(false)

// Small negative bottom margin so entrances trigger just before the content
// line, but reliably fire for fast scrolls / programmatic rendering (a -200px
// margin left below-fold sections invisible in full-page captures — see
// docs/AUDIT_REPORT.md F2).
const viewport = { once: true, margin: '0px 0px -80px', amount: 0.1 as const }

export function FadeIn(
  props: React.ComponentPropsWithoutRef<typeof motion.div>,
) {
  const shouldReduceMotion = useReducedMotion()
  const isInStaggerGroup = useContext(FadeInStaggerContext)

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 28 },
        visible: { opacity: 1, y: 0 },
      }}
      // Shared motion vocabulary (src/lib/motion.ts — pattern F1): content
      // decelerates into place. Reduced-motion users get an instant,
      // distance-free reveal via the hidden variant above.
      transition={transitions.slow}
      {...(isInStaggerGroup
        ? {}
        : {
            initial: 'hidden',
            whileInView: 'visible',
            viewport,
          })}
      {...props}
    />
  )
}

export function FadeInStagger({
  faster = false,
  ...props
}: React.ComponentPropsWithoutRef<typeof motion.div> & { faster?: boolean }) {
  return (
    <FadeInStaggerContext.Provider value={true}>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        transition={{ staggerChildren: faster ? STAGGER : STAGGER * 2 }}
        {...props}
      />
    </FadeInStaggerContext.Provider>
  )
}
